import React, { Component } from 'react'
import { app, appDatabasePrimaryFunctions } from '../../base';
import SideMenu from '../admin/SideMenu';
import TopMenu from '../admin/TopBar';
import '../../styles/css/AdminMain.css';
import '../../styles/css/PreviewPushNotifications.css';
import {
    Toast,
    ErrorMessage,
    WarningMessage,
    convertTimeStampToHumanReadable
} from '../utils/HelpfulFunction';
import { Modal } from "reactstrap";
import UploadImage from '../utils/UploadImage';
import { fitImageForPush } from '../utils/fitImageForPush';
import DatePicker from '../utils/DatePicker';
import { liveFeatures } from '../utils/featureReachability';
import { teamIdOptions } from '../utils/experienceScope';
import {
    sendImmediateNotification,
    sendImmediateNotificationToTokens,
    scheduleNotification,
    deleteScheduledNotification,
    editScheduledNotification,
    getPushStats,
    getAllFutureScheduledPushes,
    getUserByEmail,
    getPushTokensForUser
} from '../services/pushNotificationService';

// What a healthy open rate looks like, so a number on this page can be read
// as good or bad without leaving it.
//
// Deliberately a RANGE, not a single figure: the published benchmarks disagree
// because they measure different things. Airship's 2025 report puts the
// all-industry reaction rate near 7.8% (iOS ~4.9%, Android ~10.7%); Batch's
// 2025 benchmark puts generic campaigns at 4.7% and contextual ones at 16.3%;
// vendors quoting "CTR" report low single digits for media and entertainment.
// Quoting any one of them as "the" average would be false precision. What they
// agree on is the shape: mid single digits for a broadcast, Android ahead of
// iOS, and several times higher when a send is tied to a moment.
//
// Checked September 2026 — worth revisiting yearly.
export const OPEN_RATE_BENCHMARK = '3-10%';

// Everything a reader might need to interpret the numbers, in one place behind
// a "?" rather than as captions crowding the cards.
//
// `internalOnly` mirrors the cards: a club sees only Sent, so explaining
// metrics they cannot see would be noise. The platform wording is deliberately
// about "how new the fan's app build is" rather than naming iOS behaviour —
// that behaviour changes as builds carrying the notification extension roll
// out, and a help page that explains why a number is low ages badly once it
// stops being low.
export const STAT_HELP = [
    {
        title: 'How a push gets there',
        body: 'When you send, the message goes to Apple or Google, who pass it on to each phone. Neither of them tells us whether it arrived — there is no receipt. So every number here is either what they accepted from us, or what the app on the phone reported back afterwards.'
    },
    {
        title: 'Sent',
        body: 'The devices Apple and Google accepted the message for. Tokens they refuse — almost always an app that has been uninstalled — are counted separately as refused, and dropped so they are not tried again. This is the number push vendors report as "delivered".'
    },
    {
        title: 'Confirmed on device',
        internalOnly: true,
        body: 'The devices where the phone itself reported the message arriving. Whether it can depends on the platform and on how new the fan\'s app build is, so this always runs below Sent and should be read as a floor rather than as reach. An open counts as confirmed too, since a tap proves the message arrived.'
    },
    {
        title: 'Confirmed rate',
        internalOnly: true,
        body: 'Confirmed on device divided by Sent, with the iOS and Android figures underneath it. The two platforms report differently, so the split tells you more than the blended number does.'
    },
    {
        title: 'Open rate',
        internalOnly: true,
        body: 'The devices that tapped the notification, divided by Sent. This is the figure that compares to other apps, because Sent is what every push vendor calls delivered.'
    },
    {
        title: 'What a normal open rate looks like',
        internalOnly: true,
        body: 'Roughly 3-10%, with Android usually ahead of iOS. Published benchmarks disagree because they measure different things: Airship\'s 2025 report puts the all-industry reaction rate near 7.8%, Batch\'s puts generic campaigns at 4.7% against 16.3% for ones tied to a moment, and vendors quoting click-through report low single digits for media and entertainment.'
    }
];

class SetUpPushNotifications extends Component {
    constructor(props) {
        super(props);
        const todaysDateObject = new Date();
        todaysDateObject.setDate(todaysDateObject.getDate() - 30);
        this.state = {
            loading: true,
            sent: 0,
            opened: 0,
            delivered: 0,
            openRate: "0%",
            deliveredRate: "0%",
            failed: 0,
            byPlatform: null,
            incomplete: 0,
            statsHelp: false,
            searchStart: todaysDateObject,
            searchEnd: new Date(),
            campaignMessages: [],
            experiences: {},
            features: {},
            notificationExperienceId: '__all__',
            notificationAudience: 'all',
            notificationTeam: '',
            notificationImageUrl: '',
            testImageUrl: ''
        };
        this.UploadImage = new UploadImage();
        this.handleImageChange = this.handleImageChange.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.toggle = this.toggle.bind(this);
        this.toggleTestModal = this.toggleTestModal.bind(this);
        this.toggleStatsHelp = this.toggleStatsHelp.bind(this);
        this.sendTestPush = this.sendTestPush.bind(this);
    }

    async componentDidMount() {
        await this.getStats(this.state.searchStart, this.state.searchEnd);
        await this.getCampaignNotifications();
        await this.getAppIcon();
        await this.getExperiences();
    }

    async getExperiences() {
        // No flag to ask any more: a club that has no experiences reads back an
        // empty node.
        //
        // The feature flags come along because the two targeting controls answer
        // different questions. Picking an EXPERIENCE only means something when
        // fans can be in different ones — i.e. when the switcher is on. With it
        // off every fan is in the same experience by definition, so the control
        // would offer a choice that cannot exist, and worse: a device that has
        // not reopened since the club changed which experience is primary still
        // carries the old tag, so the filter would quietly miss it. Picking an
        // AUDIENCE (everyone / season ticket members) is about entitlements and
        // has nothing to do with skins at all.
        try {
            const [experiencesSnap, flagsSnap, accessSnap] = await Promise.all([
                appDatabasePrimaryFunctions.ref('experiences').once('value'),
                appDatabasePrimaryFunctions.ref('featureFlags').once('value'),
                appDatabasePrimaryFunctions.ref('featureAccess').once('value')
            ]);
            this.setState({
                experiences: experiencesSnap.val() || {},
                features: liveFeatures({ flags: flagsSnap.val() || {}, access: accessSnap.val() || {} })
            });
        } catch (e) {
            console.log(e);
        }
    }

    async getCampaignNotifications() {
        try {
            const results = await getAllFutureScheduledPushes();
            this.setState({ campaignMessages: results });
        } catch (e) {
            console.log(e);
        }
    }

    async searchData() {
        let searchStart = this.state.searchStart;
        let searchEnd = this.state.searchEnd;
        if (searchStart) {
            searchStart = searchStart.getTime();
        }
        if (searchEnd) {
            searchEnd = searchEnd.getTime();
        } else {
            searchEnd = new Date().getTime();
        }
        if (!searchStart) {
            searchStart = searchEnd - (15778800000 - 86400000);
            this.setState({ searchStart: new Date(searchStart) });
        } else if (searchStart < searchEnd - 15778800000) {
            await ErrorMessage.fire({
                title: "Hold On!",
                text: 'Search start and end must be within 6 months!'
            });
            this.setState({ loading: false });
            return;
        }
        this.setState({ loading: true });
        await this.getStats(searchStart, searchEnd);
    }

    async getStats(start, end) {
        const startMs = typeof start === 'number' ? start : new Date(start).getTime();
        const endMs = typeof end === 'number' ? end : new Date(end).getTime();

        this.setState({ loading: true });

        try {
            const stats = await getPushStats(startMs, endMs);
            this.setState({
                sent: stats.sent || 0,
                opened: stats.opened || 0,
                delivered: stats.delivered || 0,
                openRate: stats.openRate || '0%',
                deliveredRate: stats.deliveredRate || '0%',
                failed: stats.failed || 0,
                byPlatform: stats.byPlatform || null,
                incomplete: stats.incomplete || 0,
                loading: false
            });
        } catch (e) {
            this.setState({ loading: false });
            await ErrorMessage.fire({
                title: "Uh Oh!",
                text: "Error loading stats, check your internet connection and try again!"
            });
        }
    }

    async prepareNotification() {
        let { notificationLink, notificationTitle, notificationBody, notificationSendTime, schedulePush, notificationExperienceId, notificationAudience, notificationTeam, notificationImageUrl } = this.state;
        let errorMessage = "";

        if (!notificationBody || !notificationBody.trim()) {
            errorMessage = 'The notification needs a message';
        } else if (schedulePush && !notificationSendTime) {
            errorMessage = 'The notification is marked as scheduled but there is no scheduled time';
        } else if (schedulePush && notificationSendTime.getTime() <= Date.now()) {
            errorMessage = 'The scheduled time is now or before now but it is marked as scheduled. Please choose a future time.';
        } else if (notificationAudience === 'stm' && !(notificationTeam || '').trim()) {
            errorMessage = 'Please select a team for the Season Ticket Members audience';
        }
        if (errorMessage) {
            await ErrorMessage.fire({
                title: "Hold On!",
                text: errorMessage
            });
            return;
        }

        this.setState({ loading: true });

        // Who this is for, built once. It used to apply to immediate sends only
        // — not because anyone chose that, but because a scheduled push stored
        // no audience and the executor sent to everyone, so the controls were
        // hidden rather than allowed to promise something that would not hold.
        // Fields are omitted entirely unless the admin picked something other
        // than the defaults.
        const targeting = {};
        if (typeof notificationExperienceId === 'string' && notificationExperienceId !== '__all__') {
            targeting.experienceId = notificationExperienceId;
        }
        if (notificationAudience === 'stm') {
            targeting.audience = 'stm';
            targeting.team = notificationTeam;
        }

        let result;
        if (schedulePush) {
            result = await scheduleNotification(
                notificationTitle,
                notificationBody,
                notificationLink,
                notificationSendTime,
                notificationImageUrl,
                targeting
            );
        } else {
            result = await sendImmediateNotification(
                notificationTitle,
                notificationBody,
                notificationLink,
                targeting,
                notificationImageUrl
            );
        }
        // A refused send (bad image URL, nobody reached) has already shown
        // its error; leave everything typed in place so it can be fixed.
        if (!result) {
            this.setState({ loading: false });
            return;
        }

        await this.getCampaignNotifications();
        this.setState({
            loading: false,
            modal: false,
            notificationImageUrl: ''
        });
    }

    async getAppIcon() {
        const url = `https://itunes.apple.com/lookup?id=${process.env.REACT_APP_IOS_APP_ID}`;
        fetch(url)
            .then(response => response.json())
            .then(data => {
                if (data.results && data.results.length > 0) {
                    const appIconUrl = data.results[0].artworkUrl100;
                    this.setState({ appIconUrl });
                }
            })
            .catch(error => {
                this.getAppIconSecondAttempt(process.env.REACT_APP_IOS_APP_ID);
                console.error('Error fetching app data:', error);
            });
    }

    getAppIconSecondAttempt(appId) {
        const url = `https://itunes.apple.com/lookup?id=${appId}&callback=jsonCallback`;
        const script = document.createElement('script');
        script.src = url;
        window.jsonCallback = (data) => {
            if (data.results && data.results.length > 0) {
                const appIconUrl = data.results[0].artworkUrl100;
                this.setState({ appIconUrl });
            }
            document.body.removeChild(script);
            delete window.jsonCallback;
        };
        script.onerror = () => {
            document.body.removeChild(script);
            delete window.jsonCallback;
        };
        document.body.appendChild(script);
    }

    truncateText(text, limit) {
        return text.length > limit ? text.substring(0, limit) + '...' : text;
    }

    truncateTitleAndBody(title, body) {
        const combinedLimit = 178;
        if ((title.length + body.length) <= combinedLimit) {
            return { truncatedTitle: title, truncatedBody: body };
        }
        const remainingLength = combinedLimit - title.length;
        const truncatedBody = this.truncateText(body, remainingLength);
        return { truncatedTitle: title, truncatedBody };
    }

    toggle() {
        this.setState({
            modal: !this.state.modal,
            notificationLink: "",
            notificationTitle: "",
            notificationBody: "",
            notificationImageUrl: "",
            editing: false,
            schedulePush: false,
            notificationSendTime: null,
            editingItem: null,
            notificationExperienceId: '__all__',
            notificationAudience: 'all',
            notificationTeam: ''
        });
    }

    toggleStatsHelp() {
        this.setState({ statsHelp: !this.state.statsHelp });
    }

    toggleTestModal() {
        this.setState({
            testModal: !this.state.testModal,
            testMode: "token",
            testToken: "",
            testEmail: "",
            testTitle: "",
            testBody: "",
            testImageUrl: ""
        });
    }

    async sendTestPush() {
        const { testMode = "token", testToken, testEmail, testTitle, testBody } = this.state;
        if (!testBody || !testBody.trim()) {
            await ErrorMessage.fire({ title: "Hold On!", text: "Please enter a message body" });
            return;
        }

        let tokensToSend = [];
        if (testMode === "email") {
            if (!testEmail || !testEmail.trim()) {
                await ErrorMessage.fire({ title: "Hold On!", text: "Please enter a user email" });
                return;
            }
            this.setState({ loading: true });
            const user = await getUserByEmail(testEmail);
            if (!user || !user.uid) {
                this.setState({ loading: false });
                await ErrorMessage.fire({ title: "User Not Found", text: `No user found with email "${testEmail.trim()}"` });
                return;
            }
            tokensToSend = await getPushTokensForUser(user.uid);
            this.setState({ loading: false });
            if (!tokensToSend.length) {
                await ErrorMessage.fire({ title: "No Push Token", text: `User "${user.userName || testEmail.trim()}" has no registered devices.` });
                return;
            }
        } else {
            if (!testToken || !testToken.trim()) {
                await ErrorMessage.fire({ title: "Hold On!", text: "Please enter an FCM token" });
                return;
            }
            tokensToSend = [testToken.trim()];
        }

        this.setState({ testModal: false });
        await sendImmediateNotificationToTokens(tokensToSend, testTitle, testBody, '', this.state.testImageUrl);
    }

    handleChange(evt) {
        let target = evt.target;
        let value = target.type === 'checkbox' ? target.checked : target.value;
        this.setState({ [evt.target.name]: value });
    }

    // The picture rides in the push payload as a URL the phone fetches on
    // its own, so it goes through the same uploader every other screen
    // uses and only the returned URL is kept. Android shows nothing over
    // 1 MB, so a bigger photo is shrunk to a JPEG that fits first.
    async handleImageChange(evt) {
        const picked = evt.target.files && evt.target.files[0];
        evt.target.value = '';
        if (!picked) return;
        this.setState({ loading: true });
        let file;
        try {
            file = await fitImageForPush(picked);
        } catch (err) {
            this.setState({ loading: false });
            await ErrorMessage.fire({ title: 'Image cannot be used', text: err.message });
            return;
        }
        const res = await this.UploadImage.upload_file(file);
        this.setState({ loading: false });
        // The uploader hands back whatever the service answered, error bodies
        // included. Only a URL a phone can fetch is worth keeping.
        if (res.error) {
            await ErrorMessage.fire({ title: 'Image cannot be uploaded', text: res.error });
        } else if (res.imageUrl && /^https:\/\/\S+$/.test(res.imageUrl.trim())) {
            this.setState({ notificationImageUrl: res.imageUrl.trim() });
        } else {
            await ErrorMessage.fire({
                title: 'Image cannot be uploaded',
                text: 'Something went wrong, please re-upload your image and try again!'
            });
        }
    }

    async deleteScheduledPush(item) {
        // Every other list screen confirms before deleting; this one went
        // straight through on a single click, for a campaign already queued to
        // the whole fan base.
        const result = await WarningMessage.fire({
            title: 'Delete this scheduled push?',
            text: 'It will not be sent. This cannot be undone.',
            confirmButtonText: 'Yes, delete it'
        });
        if (!result || !result.value) return;
        this.setState({ loading: true });
        await deleteScheduledNotification(item.overallPushId);
        await this.getCampaignNotifications();
        this.setState({ loading: false });
    }

    async editNotification() {
        this.setState({ loading: true });
        let { notificationLink, notificationTitle, notificationBody, notificationSendTime, editingItem, notificationImageUrl } = this.state;

        const result = await editScheduledNotification(
            editingItem.overallPushId,
            notificationTitle,
            notificationBody,
            notificationLink,
            notificationSendTime,
            notificationImageUrl
        );
        if (!result) {
            this.setState({ loading: false });
            return;
        }

        await this.getCampaignNotifications();
        this.setState({ loading: false, modal: false });
    }

    editCampaign(item) {
        let link = item.link || item.deepLink || item.deeplink || '';
        const body = item.body || (item.messages && item.messages[0] && item.messages[0].body) || '';
        let title = item.title || (item.messages && item.messages[0] && item.messages[0].title) || '';
        let notificationPushTime;
        if (item.scheduledTime) {
            notificationPushTime = new Date(item.scheduledTime);
        } else if (item.push_time || item.pushTime) {
            notificationPushTime = new Date((item.push_time || item.pushTime) + "z");
        }
        this.setState({
            modal: true,
            notificationTitle: title,
            notificationBody: body,
            notificationLink: link,
            notificationImageUrl: item.imageUrl || '',
            notificationSendTime: notificationPushTime,
            schedulePush: true,
            editingItem: item,
            editing: true
        });
    }

    render() {
        const isInternal = !!app.auth().currentUser?.email?.endsWith('@sqwadhq.com');
        // Per-platform confirmation, when the range holds sends recorded with
        // a platform tally. Older pushes only know a blended number.
        const bp = this.state.byPlatform || {};
        const ios = bp.iOS || { sent: 0, deliveredRate: '0%' };
        const android = bp.android || { sent: 0, deliveredRate: '0%' };
        const platformLine = ios.sent > 0 || android.sent > 0
            ? `iOS ${ios.deliveredRate} · Android ${android.deliveredRate}`
            : 'Per-platform split from the next send';
        const campaignMessages = this.state.campaignMessages || [];
        const { truncatedTitle, truncatedBody } = this.truncateTitleAndBody(this.state.notificationTitle || '', this.state.notificationBody || '');
        const showTestPush = app.auth().currentUser?.email?.endsWith('@sqwadhq.com') || window.location.hostname === 'localhost';
        const experienceList = Object.entries(this.state.experiences || {})
            .map(([id, exp]) => ({ id, ...exp }))
            .sort((a, b) => (a.order || 0) - (b.order || 0));
        // Teams for a members-only send. A club with no experiences gets one
        // option standing for itself — a season ticket roster is a fact about
        // the club, not about how many skins it has.
        const teamExperienceList = teamIdOptions(experienceList);
        // Two questions, two gates. Which experience a fan is in only exists as
        // a question while fans can switch; who they are is always a question.
        const showExperienceTargeting = this.state.features.experienceSwitcher === true
            && experienceList.length >= 2;
        const showTargeting = showExperienceTargeting || teamExperienceList.length > 0;
        return (
            <div className="admin-wrapper">
                <div className="loading-screen" style={{ display: this.state.loading ? 'block' : 'none' }} />
                <SideMenu />
                <TopMenu />
                <div className="admin-main-panel">
                    <div className="admin-page-head">
                        <p className="admin-eyebrow">Fan engagement</p>
                        <p className="admin-header-text">Push Notifications</p>
                        <p className="admin-page-sub">
                            Messages sent to fans&rsquo; phones. A push cannot be recalled once it goes out.
                        </p>
                    </div>
                    <div className="admin-toolbar">
                        <span className="admin-toolbar-end">
                            {showTestPush &&
                                <button onClick={() => this.toggleTestModal()} className="btn btn-secondary">Send test push</button>
                            }
                            <button onClick={() => this.toggle()} className="btn btn-primary">Create push notification</button>
                        </span>
                    </div>
                    <form className="form-horizontal" style={{ marginLeft: 20, marginBottom: 10 }}>
                        <div className="row g-3 align-items-center">
                            <div className="col-auto" style={{ paddingLeft: 0 }}>
                                <DatePicker isClearable selected={this.state.searchStart} onChange={date => this.setState({ searchStart: date })} />
                            </div>
                            <div className="col-auto">
                                <DatePicker isClearable selected={this.state.searchEnd} onChange={date => this.setState({ searchEnd: date })} />
                            </div>
                            <div className="col-auto">
                                <button className="btn btn-primary btn-admin" type="button" onClick={() => this.searchData()}>Search</button>
                            </div>
                        </div>
                    </form>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '12px 24px 0' }}>
                        <button
                            type="button"
                            className="admin-icon-btn"
                            aria-label="About these numbers"
                            title="About these numbers"
                            onClick={this.toggleStatsHelp}
                        >
                            <span className="fa fa-question-circle" aria-hidden="true"/>
                        </button>
                    </div>
                    <div className="admin-statgrid" style={{margin: "8px 24px 0"}}>
                        {/* was a solid black card with 50px white text — a count is a
                            number with a label, not a slab */}
                        <div className="admin-statcard">
                            <p className="admin-eyebrow">Sent</p>
                            <p className="admin-statcard-value">{this.state.sent}</p>
                            {this.state.failed > 0 &&
                                <p className="admin-statcard-sub">{this.state.failed} refused</p>
                            }
                        </div>
                        {isInternal &&
                            <>
                                {/* "Confirmed", not "Delivered": this is the phone
                                    reporting back, not a receipt from Apple or Google.
                                    Calling it delivered made a healthy send read as
                                    half lost. The iOS/Android split is on the card
                                    below rather than explained in prose — it is a
                                    number that moves as builds carrying the
                                    notification extension roll out. */}
                                <div className="admin-statcard">
                                    <p className="admin-eyebrow">Confirmed on device</p>
                                    <p className="admin-statcard-value">{this.state.delivered}</p>
                                </div>
                                <div className="admin-statcard">
                                    <p className="admin-eyebrow">Confirmed rate</p>
                                    <p className="admin-statcard-value">{this.state.deliveredRate}</p>
                                    <p className="admin-statcard-sub">{platformLine}</p>
                                </div>
                                <div className="admin-statcard">
                                    <p className="admin-eyebrow">Open Rate</p>
                                    <p className="admin-statcard-value">{this.state.openRate}</p>
                                    <p className="admin-statcard-sub">{OPEN_RATE_BENCHMARK} typical</p>
                                </div>
                            </>
                        }
                    </div>
                    {/* Not help text — a live condition about the range on screen,
                        which belongs on the page rather than behind a "?". */}
                    {isInternal && this.state.incomplete > 0 &&
                        <p className="admin-note" style={{margin: "10px 24px 0"}}>
                            {this.state.incomplete} push{this.state.incomplete === 1 ? '' : 'es'} in this range never
                            finished sending, so Sent is short by however much was left.
                        </p>
                    }
                    <div style={{ display: 'flex', flexDirection: 'column', color: 'black', margin: 20, textAlign: "center", justifyContent: 'center', alignItems: 'center' }}>
                        {campaignMessages.length > 0 &&
                            <h3>Scheduled Messages</h3>
                        }
                        <div>
                            {campaignMessages.map((item, i) => {
                                const body = item.body || (item.messages && item.messages[0] && item.messages[0].body) || '';
                                const title = item.title || (item.messages && item.messages[0] && item.messages[0].title) || '';
                                const link = item.link || item.deepLink || item.deeplink || '';
                                const pushTime = item.scheduledTime || item.pushTime || item.push_time;
                                const displayTime = pushTime
                                    ? convertTimeStampToHumanReadable(new Date(pushTime).getTime().toString())
                                    : 'Unknown time';

                                return (
                                    <div style={{ display: 'flex', margin: 20 }} key={i}>
                                        <div style={{ border: "1px black dotted", padding: 10, minWidth: 400, textAlign: 'center' }}>
                                            <div>Push notification scheduled for:</div>
                                            <div>{displayTime}</div>
                                            {title && <div>Title: {title}</div>}
                                            <div>Message: {body}</div>
                                            {link && <div>Link: {link}</div>}
                                            <div style={{ display: "flex", flexDirection: 'row', justifyContent: "space-around" }}>
                                                <button type="button" className="admin-icon-btn" aria-label="Edit scheduled push" title="Edit scheduled push" onClick={() => this.editCampaign(item)}><span className="fa fa-pencil" aria-hidden="true"/></button>
                                                <button type="button" className="admin-icon-btn is-danger" aria-label="Delete scheduled push" title="Delete scheduled push" onClick={() => this.deleteScheduledPush(item)}><span className="fa fa-trash-o" aria-hidden="true"/></button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <Modal isOpen={this.state.statsHelp} toggle={this.toggleStatsHelp} style={{ maxWidth: 620 }}>
                    <div style={{ padding: 24 }}>
                        <h4 style={{ marginTop: 0 }}>About these numbers</h4>
                        {STAT_HELP
                            .filter(section => isInternal || !section.internalOnly)
                            .map(section => (
                                <div key={section.title} style={{ marginBottom: 16 }}>
                                    <p className="admin-eyebrow" style={{ marginBottom: 4 }}>{section.title}</p>
                                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: '#333' }}>{section.body}</p>
                                </div>
                            ))}
                        <button type="button" className="btn btn-primary" onClick={this.toggleStatsHelp}>Close</button>
                    </div>
                </Modal>

                <Modal isOpen={this.state.modal} toggle={this.toggle} style={{ width: '90%' }} id="myModal">
                    <div className="card">
                        <div className="card-body">
                            <h4 className="header-title mb-3"> Send Push Notification Form</h4>
                            <div id="rootwizard">
                                <div className="tab-content mb-0 b-0">
                                    <div className="tab-pane active show" id="first">
                                        <form id="accountForm" method="post" action="#" className="form-horizontal">
                                            <div className="row">
                                                <div className="col-12">
                                                    <div className="form-group row mb-3">
                                                        <label className="col-md-3 col-form-label" htmlFor="notificationTitle"> Title (optional)</label>
                                                        <div className="col-md-9">
                                                            <input id="notificationTitle" name="notificationTitle" type="text" className="form-control" value={this.state.notificationTitle} onChange={this.handleChange} placeholder="My First Push Notification" />
                                                        </div>
                                                    </div>
                                                    <div className="form-group row mb-3">
                                                        <label className="col-md-3 col-form-label" htmlFor="notificationBody"> Message</label>
                                                        <div className="col-md-9">
                                                            <textarea name="notificationBody" id="notificationBody" value={this.state.notificationBody} onChange={this.handleChange} className="form-control" placeholder="Notification message here..." />
                                                        </div>
                                                    </div>
                                                    <div className="form-group row mb-3">
                                                        <label className="col-md-3 col-form-label" htmlFor="notificationLink"> Website To Link To (optional)</label>
                                                        <div className="col-md-9">
                                                            <input id="notificationLink" name="notificationLink" type="url" className="form-control" value={this.state.notificationLink} onChange={this.handleChange} placeholder="https://yournotificationlink.com" />
                                                        </div>
                                                    </div>
                                                    <div className="form-group row mb-3">
                                                        <label className="col-md-3 col-form-label" htmlFor="notificationImage"> Image (optional)</label>
                                                        <div className="col-md-9">
                                                            <input id="notificationImage" name="notificationImage" type="file" accept="image/png,image/jpeg,image/gif" className="form-control" onChange={this.handleImageChange} />
                                                            <p className="admin-statcard-sub">Shown large under the message on both platforms. JPG, PNG or GIF; landscape works best. Photos over 1 MB are shrunk to fit (Android shows nothing bigger). iPhones on a build older than the one with rich pushes show the message without it.</p>
                                                            {this.state.notificationImageUrl ? (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                                                                    <img src={this.state.notificationImageUrl} alt="Push preview" style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 4 }} />
                                                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => this.setState({ notificationImageUrl: '' })}>Remove image</button>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                    <div className="form-check">
                                                        <input value={this.state.schedulePush} className="form-check-input" id="schedulePush" name="schedulePush" type="checkbox" checked={this.state.schedulePush} onChange={this.handleChange} />
                                                        <label className="form-check-label" htmlFor="schedulePush">Schedule Push</label>
                                                    </div>
                                                    {this.state.schedulePush &&
                                                        <div className="row g-3 align-items-center" style={{ marginBottom: 20, marginTop: 10, marginLeft: 20 }}>
                                                            <div className="col-auto" style={{ paddingLeft: 0 }}>
                                                                <DatePicker minWidth={250} showTimeInput dateFormat="Pp" isClearable selected={this.state.notificationSendTime} onChange={date => this.setState({ notificationSendTime: date })} />
                                                            </div>
                                                        </div>
                                                    }
                                                    {showTargeting &&
                                                        <div style={{ borderTop: '1px solid #e5e5e5', marginTop: 15, paddingTop: 15 }}>
                                                            <label style={{ fontWeight: 'bold' }}>Targeting</label>
                                                            <p style={{ fontSize: 12, color: 'grey', marginBottom: 10 }}>Applies whether you send now or schedule it</p>
                                                            {showExperienceTargeting &&
                                                            <div className="form-group row mb-3">
                                                                <label className="col-md-3 col-form-label" htmlFor="notificationExperienceId">Experience</label>
                                                                <div className="col-md-9">
                                                                    <select id="notificationExperienceId" name="notificationExperienceId" className="form-control" value={this.state.notificationExperienceId} onChange={this.handleChange}>
                                                                        <option value="__all__">All experiences</option>
                                                                        {experienceList.map(exp => (
                                                                            <option key={exp.id} value={exp.isDefault ? '' : exp.id}>{exp.name}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            }
                                                            <div className="form-group row mb-3">
                                                                <label className="col-md-3 col-form-label" htmlFor="notificationAudience">Audience</label>
                                                                <div className="col-md-9">
                                                                    <select id="notificationAudience" name="notificationAudience" className="form-control" value={this.state.notificationAudience} onChange={this.handleChange}>
                                                                        <option value="all">Everyone</option>
                                                                        <option value="stm">Season Ticket Members only</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            {this.state.notificationAudience === 'stm' &&
                                                                <div className="form-group row mb-3">
                                                                    <label className="col-md-3 col-form-label" htmlFor="notificationTeam">Team</label>
                                                                    <div className="col-md-9">
                                                                        <select id="notificationTeam" name="notificationTeam" className="form-control" value={this.state.notificationTeam} onChange={this.handleChange}>
                                                                            {/* team must always be a REAL experience id — it keys entitlements/{uid}/{team} */}
                                                                            <option value="">Select a team…</option>
                                                                            {teamExperienceList.map(exp => (
                                                                                <option key={exp.id} value={exp.id}>{exp.name}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            }
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </form>
                                        {/* This panel exists to be accurate about what a fan
                                            gets. Rich pushes made it wrong: an operator attached a
                                            picture, saw a preview without one, and had no reason to
                                            believe it had worked. The thumbnail by the upload field
                                            confirms the FILE arrived; this shows the NOTIFICATION. */}
                                        <div className="notification-preview">
                                            <div className="notification-preview-header">Notification Preview</div>
                                            <div className="notification-preview-body">
                                                <img className="notification-preview-icon" src={this.state.appIconUrl} alt="App Icon" />
                                                <div className="notification-preview-content">
                                                    <div className="notification-preview-title">{truncatedTitle}</div>
                                                    <div className="notification-preview-message" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{truncatedBody}</div>
                                                </div>
                                            </div>
                                            {this.state.notificationImageUrl &&
                                                <img
                                                    className="notification-preview-image"
                                                    src={this.state.notificationImageUrl}
                                                    alt=""
                                                />
                                            }
                                        </div>
                                        {this.state.notificationImageUrl &&
                                            <p className="notification-preview-note">
                                                Shown expanded. On the lock screen a fan sees the text first &mdash;
                                                Android collapses the picture until they pull the notification down,
                                                iPhones show a thumbnail until they press and hold.
                                            </p>
                                        }
                                    </div>
                                    <ul className="list-inline wizard mb-0" style={{ marginTop: 20 }}>
                                        {/* was an <a href="#"> styled as a button: it navigated
                                            as well as sending, and a push cannot be recalled */}
                                        <button type="button" className="btn btn-primary creategame-wizard-button" onClick={() => this.state.editing ? this.editNotification() : this.prepareNotification()}>
                                            {this.state.schedulePush ? "Schedule push" : "Send notification"}
                                        </button>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
                {showTestPush &&
                    <Modal isOpen={this.state.testModal} toggle={this.toggleTestModal} style={{ width: '90%' }}>
                        <div className="card">
                            <div className="card-body">
                                <h4 className="header-title mb-3">Send Test Push Notification</h4>
                                <form className="form-horizontal">
                                    <div className="form-group row mb-3">
                                        <label className="col-md-3 col-form-label">Target By</label>
                                        <div className="col-md-9">
                                            <label className="radio-inline" style={{ marginRight: 15 }}>
                                                <input type="radio" name="testMode" value="token" checked={(this.state.testMode || 'token') === 'token'} onChange={this.handleChange} /> FCM Token
                                            </label>
                                            <label className="radio-inline">
                                                <input type="radio" name="testMode" value="email" checked={this.state.testMode === 'email'} onChange={this.handleChange} /> User Email
                                            </label>
                                        </div>
                                    </div>
                                    {(this.state.testMode || 'token') === 'token' ? (
                                        <div className="form-group row mb-3">
                                            <label className="col-md-3 col-form-label" htmlFor="testToken">FCM Token</label>
                                            <div className="col-md-9">
                                                <textarea id="testToken" name="testToken" className="form-control" rows="3" value={this.state.testToken} onChange={this.handleChange} placeholder="Paste FCM token here..." />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="form-group row mb-3">
                                            <label className="col-md-3 col-form-label" htmlFor="testEmail">User Email</label>
                                            <div className="col-md-9">
                                                <input id="testEmail" name="testEmail" type="email" className="form-control" value={this.state.testEmail || ''} onChange={this.handleChange} placeholder="user@example.com" />
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-group row mb-3">
                                        <label className="col-md-3 col-form-label" htmlFor="testTitle">Title (optional)</label>
                                        <div className="col-md-9">
                                            <input id="testTitle" name="testTitle" type="text" className="form-control" value={this.state.testTitle} onChange={this.handleChange} placeholder="Test notification title" />
                                        </div>
                                    </div>
                                    <div className="form-group row mb-3">
                                        <label className="col-md-3 col-form-label" htmlFor="testBody">Message</label>
                                        <div className="col-md-9">
                                            <textarea id="testBody" name="testBody" className="form-control" value={this.state.testBody} onChange={this.handleChange} placeholder="Test notification message..." />
                                        </div>
                                    </div>
                                    <div className="form-group row mb-3">
                                        <label className="col-md-3 col-form-label" htmlFor="testImageUrl">Image URL (optional)</label>
                                        <div className="col-md-9">
                                            <input id="testImageUrl" name="testImageUrl" type="url" className="form-control" value={this.state.testImageUrl} onChange={this.handleChange} placeholder="https://.../picture.jpg" />
                                        </div>
                                    </div>
                                </form>
                                <button type="button" className="btn btn-primary creategame-wizard-button" onClick={() => this.sendTestPush()}>Send test push</button>
                            </div>
                        </div>
                    </Modal>
                }
            </div>
        );
    }
}

export default SetUpPushNotifications;
